import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import WidgetPreview from 'components/quote_builder_widget/WidgetPreview';
import CssEditorModal from 'components/quote_builder_widget/CssEditorModal';
import SettingsSidebar from 'components/quote_builder_widget/SettingsSidebar';
import { ConfigurationSection, AppearanceSection, CartonMaterialsSectionWrapper, CorrugatedMaterialsSectionWrapper } from 'components/quote_builder_widget/sections';

const SAMPLE_TEMPLATE_IDENTIFIER = 'becf-12017';

// Sample drawings available for preview, organized by main group
const PREVIEW_DRAWINGS = [
  { value: 'becf-12017', label: 'Cartons - Food Boxes (becf-12017)' },
  { value: 'becf-21712', label: 'Corrugated Cardboards - Tray Boxes (becf-21712)' },
  { value: 'becf-30101', label: 'Hard Cardboards - Covered Solid Board Boxes (becf-30101)' },
];

// Themes that support custom CSS
const THEMES_WITH_CUSTOM_CSS = ['dark', 'default', 'minimal', 'compact'];

// Map user's default unit to widget unit option
const getDefaultUnitOption = (defaultUnit) => {
  if (defaultUnit === 'in') {
    return 'in';
  }
  return 'mm';
};

const FormNew = ({ identifier, setMode, defaultUnit = 'mm' }) => {
  const initialFetchDone = useRef(false);
  const [activeSection, setActiveSection] = useState('configuration');
  const [domains, setDomains] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [highlightColor, setHighlightColor] = useState(getThemeDefaultHighlightColor('default'));
  const [submitButtonColor, setSubmitButtonColor] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const previousThemeRef = useRef('default');
  const [fontFamily, setFontFamily] = useState('');
  const [unitOption, setUnitOption] = useState(identifier ? 'both' : getDefaultUnitOption(defaultUnit));
  const [customCss, setCustomCss] = useState('');
  const customCssRef = useRef('');
  const [customCssByTheme, setCustomCssByTheme] = useState({});
  const customCssByThemeRef = useRef({});
  const [isCssEditorOpen, setIsCssEditorOpen] = useState(false);
  const [showCartonMaterials, setShowCartonMaterials] = useState(true);
  const [showCorrugatedCardboardMaterials, setShowCorrugatedCardboardMaterials] = useState(true);

  const [addDomainFormVisible, setAddDomainFormVisible] = useState(false);
  const [addDomainFormHighlighted, setAddDomainFormHighlighted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [savedIdentifier, setSavedIdentifier] = useState(identifier);
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [previewDrawing, setPreviewDrawing] = useState(SAMPLE_TEMPLATE_IDENTIFIER);

  const supportsCustomCss = THEMES_WITH_CUSTOM_CSS.includes(selectedTheme);

  // Send style updates to widget preview iframe for live preview
  const sendStyleUpdate = (type, data) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage({ type, ...data }, '*');
      } catch (error) {
        // Ignore cross-origin errors
      }
    });
  };

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_background_color', { color: backgroundColor });
    }
  }, [backgroundColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_font_family', { fontFamily: fontFamily });
    }
  }, [fontFamily, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_highlight_color', { color: highlightColor });
    }
  }, [highlightColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_submit_button_color', { color: submitButtonColor || highlightColor });
    }
  }, [submitButtonColor, highlightColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    customCssRef.current = customCss;
  }, [customCss]);

  useEffect(() => {
    customCssByThemeRef.current = customCssByTheme;
  }, [customCssByTheme]);

  // Update highlight color and switch to theme-specific CSS when theme changes
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    const previousTheme = previousThemeRef.current;
    const previousDefault = getThemeDefaultHighlightColor(previousTheme);
    const newDefault = getThemeDefaultHighlightColor(selectedTheme);

    if (highlightColor === previousDefault && previousDefault !== newDefault) {
      setHighlightColor(newDefault);
    }

    if (previousTheme !== selectedTheme) {
      setCustomCssByTheme((previous) => ({
        ...previous,
        [previousTheme]: customCssRef.current
      }));
      setCustomCss(customCssByThemeRef.current[selectedTheme] || '');
    }

    previousThemeRef.current = selectedTheme;
  }, [selectedTheme]);

  const fetchWidget = () => {
    if (identifier === undefined) {
      return;
    }

    GetRequest({
      url: `/quote-builder/user-quote-builder-widgets/${identifier}`,
      callback: (data) => {
        const theme = data.properties.theme || 'default';
        setDomains(data.domains);
        setSelectedTheme(theme);
        setHighlightColor(data.properties.highlight_color || getThemeDefaultHighlightColor(theme));
        setSubmitButtonColor(data.properties.submit_button_color || '');
        setBackgroundColor(data.properties.background_color || '#ffffff');
        setFontFamily(data.properties.font_family || '');
        setUnitOption(data.properties.unit_option || 'both');
        setCustomCss(data.custom_css || '');
        customCssRef.current = data.custom_css || '';
        setCustomCssByTheme(data.custom_css_by_theme || {});
        customCssByThemeRef.current = data.custom_css_by_theme || {};
        setShowCartonMaterials(data.properties.show_carton_materials !== false);
        setShowCorrugatedCardboardMaterials(data.properties.show_corrugated_cardboard_materials !== false);
        previousThemeRef.current = theme;
        setTimeout(() => {
          initialFetchDone.current = true;
        }, 100);
      }
    });
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setMode();
  };

  const saveWidget = (overrides = {}) => {
    const domainsToSave = overrides.domains !== undefined ? overrides.domains : domains;

    const saveData = {
      identifier: savedIdentifier,
      domains: domainsToSave,
      theme: selectedTheme,
    };

    if (supportsCustomCss) {
      saveData.highlight_color = highlightColor;
      saveData.submit_button_color = submitButtonColor;
      saveData.background_color = backgroundColor;
      saveData.font_family = fontFamily;
      saveData.custom_css = overrides.customCss !== undefined ? overrides.customCss : customCss;
    }

    saveData.unit_option = unitOption;
    saveData.show_carton_materials = showCartonMaterials;
    saveData.show_corrugated_cardboard_materials = showCorrugatedCardboardMaterials;

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      data: saveData,
      callback: (data) => {
        setSuccessMessage(t('quote_builder_widget.saved_successfully'));
        setPreviewKey(Date.now());
        if (!savedIdentifier) {
          setSavedIdentifier(data.identifier);
          window.history.replaceState({}, '', `/quote-builder/widgets/${data.identifier}/edit`);
        }
      },
      400: (data) => {
        setErrorMessage(data.error);
      }
    });
  };

  // Auto-save when domains change (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    if (domains.length === 0) {
      return;
    }
    saveWidget();
  }, [domains]);

  // Auto-save when showCartonMaterials changes (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    saveWidget();
  }, [showCartonMaterials]);

  // Auto-save when showCorrugatedCardboardMaterials changes (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    saveWidget();
  }, [showCorrugatedCardboardMaterials]);

  const handleSave = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (domains.length === 0) {
      setActiveSection('configuration');
      setAddDomainFormVisible(true);
      setAddDomainFormHighlighted(true);
      return;
    }

    saveWidget();
  };

  useEffect(fetchWidget, [identifier]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'configuration':
        return (
          <ConfigurationSection
            domains={domains}
            setDomains={setDomains}
            unitOption={unitOption}
            setUnitOption={setUnitOption}
            addDomainFormVisible={addDomainFormVisible}
            setAddDomainFormVisible={setAddDomainFormVisible}
            addDomainFormHighlighted={addDomainFormHighlighted}
            setAddDomainFormHighlighted={setAddDomainFormHighlighted}
            newDomain={newDomain}
            setNewDomain={setNewDomain}
          />
        );
      case 'appearance':
        return (
          <AppearanceSection
            selectedTheme={selectedTheme}
            setSelectedTheme={setSelectedTheme}
            highlightColor={highlightColor}
            setHighlightColor={setHighlightColor}
            submitButtonColor={submitButtonColor}
            setSubmitButtonColor={setSubmitButtonColor}
            backgroundColor={backgroundColor}
            setBackgroundColor={setBackgroundColor}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            customCss={customCss}
            supportsCustomCss={supportsCustomCss}
            onOpenCssEditor={() => setIsCssEditorOpen(true)}
          />
        );
      case 'carton_materials':
        return (
          <CartonMaterialsSectionWrapper
            showCartonMaterials={showCartonMaterials}
            setShowCartonMaterials={setShowCartonMaterials}
            onMaterialsChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'corrugated_materials':
        return (
          <CorrugatedMaterialsSectionWrapper
            showCorrugatedCardboardMaterials={showCorrugatedCardboardMaterials}
            setShowCorrugatedCardboardMaterials={setShowCorrugatedCardboardMaterials}
            onMaterialsChange={() => setPreviewKey(Date.now())}
          />
        );
      default:
        return null;
    }
  };

  const containerStyle = {
    display: 'flex',
    gap: '0',
    alignItems: 'stretch',
    minHeight: '600px',
  };

  const leftPanelStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 auto',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid #dee2e6',
    backgroundColor: '#fff',
  };

  const innerContainerStyle = {
    display: 'flex',
    flex: '1',
  };

  const contentStyle = {
    flex: '0 1 450px',
    padding: '24px',
    minWidth: '300px',
    maxWidth: '450px',
  };

  const previewContainerStyle = {
    flex: '1 1 400px',
    minWidth: '380px',
    marginLeft: '20px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    alignSelf: 'flex-start',
  };

  const footerStyle = {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #eee',
  };

  return (
    <div>
      {/* Messages */}
      {errorMessage !== '' && (
        <div className="alert alert-danger fade in" style={{ marginBottom: '20px' }}>
          <strong><i className="fa fa-exclamation-circle"></i> {errorMessage}</strong>
        </div>
      )}
      {successMessage !== '' && (
        <div className="alert alert-success fade in" style={{ marginBottom: '20px' }}>
          <strong><i className="fa fa-check-circle"></i> {successMessage}</strong>
        </div>
      )}

      {/* Main Layout */}
      <div style={containerStyle}>
        {/* Left Panel: Header + Sidebar + Content */}
        <div style={leftPanelStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>
              {identifier ? t('quote_builder_widget.update_title') : t('quote_builder_widget.new_title')}
            </h3>
          </div>

          {/* Inner container for Sidebar + Content */}
          <div style={innerContainerStyle}>
            {/* Sidebar */}
            <SettingsSidebar
              activeSection={activeSection}
              onSectionChange={setActiveSection}
            />

            {/* Content Area */}
            <div style={contentStyle}>
              {renderActiveSection()}

              {/* Footer with Save button */}
              <div style={footerStyle}>
                <button className="btn simple-btn" type="button" onClick={handleSave}>
                  {t('quote_builder_widget.save')}
                </button>
                {' '}
                <button className="btn" onClick={handleCancel}>
                  {t('quote_builder_widget.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        {savedIdentifier && (
          <div style={previewContainerStyle}>
            <h5 style={{ marginTop: 0, marginBottom: '15px' }}>{t('quote_builder_widget.preview')}</h5>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', color: '#666' }}>
                {t('quote_builder_widget.preview_drawing') || 'Preview Drawing'}
              </label>
              <select
                className="form-control"
                value={previewDrawing}
                onChange={(event) => {
                  setPreviewDrawing(event.target.value);
                  setPreviewKey(Date.now());
                }}
                style={{ width: '100%', fontSize: '13px' }}
              >
                {PREVIEW_DRAWINGS.map((drawing) => (
                  <option key={drawing.value} value={drawing.value}>
                    {drawing.label}
                  </option>
                ))}
              </select>
            </div>
            <hr style={{ margin: '15px 0' }} />
            <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', backgroundColor: '#fff' }}>
              <WidgetPreview
                identifier={savedIdentifier}
                templateIdentifier={previewDrawing}
                previewKey={previewKey}
              />
            </div>
          </div>
        )}
      </div>

      {supportsCustomCss && (
        <CssEditorModal
          isOpen={isCssEditorOpen}
          onClose={() => setIsCssEditorOpen(false)}
          onSave={(newCss) => {
            setCustomCss(newCss);
            setCustomCssByTheme((previous) => ({
              ...previous,
              [selectedTheme]: newCss
            }));
            setIsCssEditorOpen(false);
            saveWidget({ customCss: newCss });
          }}
          currentCss={customCss}
          theme={selectedTheme}
          domains={domains}
        />
      )}
    </div>
  );
};

export default FormNew;
